import type { OfferArtifacts, OfferChannel, OfferRenderFailureCode, OfferStatus } from '@today/types'
import { apiClient } from './api.client'
import type { Paged } from './customer.service'

/** Offer API client (TO-123, TO-466). Shapes mirror the offer-service DTO (camelCase). */

export interface OfferDTO {
	offerId: string
	tenantId: string
	customerId: string
	createdBy: string
	status: OfferStatus
	locale: string
	/** The manager's message (TO-124); null when none. */
	introduction: string | null
	currentRenderId: string
	createdAt: string
	updatedAt: string
	productIds: string[]
	/** Per-artifact readiness of the in-flight render; absent from an offer-service older than TO-466. */
	artifacts?: OfferArtifacts
	/** The live (published) version; null until the first send's join (TO-464). */
	currentVersion: number | null
	/** The version rendering now (SENDING, or a SENT re-version); null when idle. */
	pendingRenderVersion: number | null
	/** Why the last render did not publish; null once a newer render is requested. */
	renderFailure: { code: OfferRenderFailureCode; renderVersion: number; failedAt: string } | null
	/** The channels of the last send (TO-465); Re-send reuses them. */
	sendChannels: OfferChannel[]
	/** The landing call-to-action; both null on a minimal draft until Step 3 sets it (TO-125). */
	ctaLabel: string | null
	ctaHref: string | null
	/** When the offer's link stops working (ISO instant); null = no expiry (TO-125). */
	expiresAt: string | null
	/** The linked notification template (TO-140); null when none. Absent from an offer-service older than TO-140. */
	notificationTemplateId?: string | null
	/** The custom notification message (TO-140); null when none. Never set together with the link. */
	notificationBody?: string | null
}

/** `POST /offers/{id}/send` `202` body. */
export interface OfferSendAccepted {
	offerId: string
	renderId: string
	renderVersion: number
	status: OfferStatus
}

/** `POST /offers` body (mirrors offer-service's `offerCreateSchema`); only `customerId` is required (TO-125). */
export interface OfferCreateBody {
	customerId: string
	productIds?: string[]
	locale?: string
	cta?: { label: string; href: string }
	introduction?: string
	expiresAt?: string | null
}

/** The `PATCH /offers/{id}` fields Step 4 edits (TO-125). */
export interface OfferPatchBody {
	expiresAt?: string | null
}

/** `PATCH /offers/{id}` body as Step 3 autosaves it (TO-124): only the changed fields. */
export interface OfferMessagePatch {
	introduction?: string | null
	locale?: string
	/** The notification message (TO-140): link a template, or store a custom body — never both non-null. */
	notificationTemplateId?: string | null
	notificationBody?: string | null
}

/** `POST /offers/{id}/preview` body (TO-124): the unsaved values to render; both optional (mirrors the server schema, `{}` previews the stored offer). */
export interface OfferPreviewBody {
	locale?: string
	introduction?: string | null
}

/** `GET /offers` `sort` values (mirror offer-service's `OfferSortField`, TO-657). */
export enum OfferListSort {
	CREATED_AT = 'createdAt',
	SENT_AT = 'sentAt',
	STATUS = 'status',
}

/** `GET /offers` `order` values; the server defaults to ascending. */
export enum SortOrder {
	ASC = 'asc',
	DESC = 'desc',
}

/** `GET /offers` arguments (TO-657 REQ-012): unset filters / sort / order are not sent. */
export interface OfferListParams {
	page: number
	limit: number
	filters?: { status?: OfferStatus }
	sort?: OfferListSort
	order?: SortOrder
}

const offer = (id: string) => `/offers/${encodeURIComponent(id)}`

/** The list arguments as `GET /offers` query parameters; unset ones are left out so a `{ page, limit }` call is unchanged. */
const listParamsOf = ({ page, limit, filters, sort, order }: OfferListParams): Record<string, string | number> => {
	const params: Record<string, string | number> = { page, limit }
	if (filters?.status !== undefined) params['filters[status]'] = filters.status
	if (sort !== undefined) params.sort = sort
	if (order !== undefined) params.order = order
	return params
}

export const offerService = {
	/** `GET /offers` — one page of the tenant's offers, optionally one status and a sort (TO-657). */
	list: async (params: OfferListParams) =>
		(await apiClient.get<Paged<OfferDTO>>('/offers', { params: listParamsOf(params) })).data,
	/** `GET /offers/{id}`. */
	get: async (id: string) => (await apiClient.get<OfferDTO>(offer(id))).data,
	create: async (body: OfferCreateBody) => (await apiClient.post<OfferDTO>('/offers', body)).data,
	/** `POST /offers/{id}/send` — DRAFT/SEND_FAILED → SENDING over 1–3 channels (TO-464/TO-465). */
	send: async (id: string, channels: OfferChannel[]) =>
		(await apiClient.post<OfferSendAccepted>(`${offer(id)}/send`, { channels })).data,
	/** `GET /offers/send-channels` — the tenant's enabled offer channels, in declaration order (TO-465). */
	sendChannels: async () => (await apiClient.get<{ channels: OfferChannel[] }>('/offers/send-channels')).data.channels,
	/** `PATCH /offers/{id}` — edits an unsent offer in place: Step 3's message/locale autosave (TO-124) and Step 4's expiry (TO-125). */
	update: async (id: string, patch: OfferMessagePatch | OfferPatchBody) =>
		(await apiClient.patch<OfferDTO>(offer(id), patch)).data,
	/** `POST /offers/{id}/preview` — the landing the next render would store, as HTML (TO-124). */
	preview: async (id: string, body: OfferPreviewBody) =>
		(await apiClient.post<{ html: string }>(`${offer(id)}/preview`, body)).data.html,
	/** `POST /offers/{id}/transitions` — a manual lifecycle move (TO-125: SEND_FAILED → ERROR is "discard"). */
	transition: async (id: string, targetStatus: OfferStatus) =>
		(await apiClient.post<OfferDTO>(`${offer(id)}/transitions`, { targetStatus })).data,
	/** `PUT /offers/{id}/products` — the full ordered product set of a DRAFT. */
	replaceProducts: async (id: string, productIds: string[]) =>
		(await apiClient.put<OfferDTO>(`${offer(id)}/products`, { productIds })).data,
}

/** TanStack query keys — all under `['offers']`, the prefix offer realtime events invalidate. */
export const offerKeys = {
	all: ['offers'] as const,
	/** Keyed on the query actually sent: equal requests share an entry, differently filtered lists never do. */
	list: (params: OfferListParams) => ['offers', 'list', listParamsOf(params)] as const,
	detail: (id: string) => ['offers', 'detail', id] as const,
}
